import { Geotab, StatusData } from "@fairfleet/geotab";
import { RESULTS_LIMIT_STATUS_DATA } from "@/constants/resultsLimits";

/**
 * Equal to the Geotab client's default `queueMaxSize`. The client holds queued Gets for up to
 * 1.5 s unless the queue fills, so a smaller round would wait out the full timer for every round
 * and turn one `ExecuteMultiCall` into many sequential flushes.
 */
export const DEFAULT_STATUS_DATA_CONCURRENCY = 100;

/** One series to fetch: a device, optionally narrowed to a single diagnostic. */
export interface StatusDataFetchTask {
  /** Identifier the caller uses to route each page back to the right series. */
  key: string;
  /** Omit to fetch the diagnostic for every device at once (then `diagnosticId` should be set). */
  deviceId?: string;
  /** Omit to fetch all diagnostics of the device. */
  diagnosticId?: string;
  /**
   * Without a `deviceId`: narrow the series to the vehicles of these groups
   * (`deviceSearch.groups`, which accepts GroupSearch objects carrying only an `id`).
   */
  groupIds?: string[];
}

function deviceSearchOf(task: StatusDataFetchTask): {
  deviceSearch?: { id?: string; groups?: { id: string }[] };
} {
  if (task.deviceId) return { deviceSearch: { id: task.deviceId } };
  if (task.groupIds?.length) return { deviceSearch: { groups: task.groupIds.map(id => ({ id })) } };
  return {};
}

export interface FetchStatusDataCompleteOptions {
  tasks: StatusDataFetchTask[];
  fromDate: Date;
  toDate: Date;
  /** Called with each page as it arrives, so callers can render partial results. */
  onPage: (key: string, page: StatusData[]) => void;
  /**
   * Called with a task's key once every page of that series has arrived, and not when it fails.
   * Lets a caller treat one series as done without waiting for the slowest of the others.
   */
  onSeriesComplete?: (key: string) => void;
  resultsLimit?: number;
  signal?: AbortSignal;
  /** Maximum number of series fetching in parallel (default {@link DEFAULT_STATUS_DATA_CONCURRENCY}). */
  maxConcurrency?: number;
}

/**
 * Fetches all `StatusData` for a set of series within `[fromDate, toDate]`.
 *
 * A single `Get` per series is not enough. Geotab returns records oldest first and silently
 * truncates at `resultsLimit`, so a busy device or a frequently reported diagnostic loses the
 * newest — and usually most interesting — part of the window. Whenever a page comes back full,
 * the series is fetched again, until the window is covered: one vehicle's series starting just
 * after its newest record, a series across vehicles with Geotab's sort and `lastId`.
 *
 * Series are limited to `maxConcurrency` in flight. The default fills one client queue flush, so
 * the first page of up to that many series still travels in a single `ExecuteMultiCall`. The cap
 * only bounds queued work and paging fan-out; the client's own per-minute call budget
 * (`rateLimit`, default 900 per minute) is unchanged.
 *
 * @param geotabClient - The Geotab client to call through.
 * @param options - See {@link FetchStatusDataCompleteOptions}.
 */
export async function fetchStatusDataComplete(
  geotabClient: Geotab,
  options: FetchStatusDataCompleteOptions,
): Promise<void> {
  const { tasks, fromDate, toDate, onPage, onSeriesComplete, signal } = options;
  const resultsLimit = options.resultsLimit ?? RESULTS_LIMIT_STATUS_DATA;
  const maxConcurrency = options.maxConcurrency ?? DEFAULT_STATUS_DATA_CONCURRENCY;

  if (!Number.isInteger(maxConcurrency) || maxConcurrency < 1) {
    throw new RangeError(`maxConcurrency must be a positive integer, got ${maxConcurrency}`);
  }

  const unbounded = tasks.find(task => !task.deviceId && !task.diagnosticId);
  if (unbounded) {
    throw new RangeError(
      `StatusData task "${unbounded.key}" has neither deviceId nor diagnosticId`,
    );
  }

  signal?.throwIfAborted();

  // One failed series aborts the rest. Without this the remaining series keep paging after the
  // caller has already received the rejection, spending requests on a result nobody will read.
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(signal?.reason);
  signal?.addEventListener("abort", abortFromCaller, { once: true });

  try {
    let nextIndex = 0;

    // A failing worker aborts the shared signal and rethrows, so Promise.all rejects with the
    // first error immediately instead of waiting for in-flight series that ignore the abort.
    const worker = async (): Promise<void> => {
      while (nextIndex < tasks.length) {
        const task = tasks[nextIndex++];
        try {
          await fetchSeries(
            geotabClient,
            task,
            fromDate,
            toDate,
            resultsLimit,
            onPage,
            controller.signal,
          );
          controller.signal.throwIfAborted();
          onSeriesComplete?.(task.key);
        } catch (error) {
          controller.abort(error);
          throw error;
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(maxConcurrency, tasks.length) }, worker));
  } finally {
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

async function fetchSeries(
  geotabClient: Geotab,
  task: StatusDataFetchTask,
  fromDate: Date,
  toDate: Date,
  resultsLimit: number,
  onPage: FetchStatusDataCompleteOptions["onPage"],
  signal: AbortSignal,
): Promise<void> {
  if (!task.deviceId) {
    return fetchSortedSeries(geotabClient, task, fromDate, toDate, resultsLimit, onPage, signal);
  }

  let pageFromDate = fromDate;

  while (true) {
    signal.throwIfAborted();

    const response = await geotabClient.get(
      "StatusData",
      {
        ...deviceSearchOf(task),
        ...(task.diagnosticId ? { diagnosticSearch: { id: task.diagnosticId } } : {}),
        fromDate: pageFromDate,
        toDate,
      },
      resultsLimit,
      signal,
    );
    const page = checkedPage(response, task, signal);
    onPage(task.key, page);

    if (page.length < resultsLimit) {
      return;
    }

    // The page was truncated, so continue just past its newest record. Records sharing that
    // exact millisecond beyond the limit are the one case this cannot recover; a page of
    // 50 000 records inside a single millisecond does not occur in practice.
    const newestMs = page.reduce((max, record) => {
      // A single unparseable timestamp must not poison the fold — with NaN in it, paging
      // would stop and the newest part of the window would go missing silently, which is
      // the very failure this module exists to prevent.
      const recordMs = new Date(record.dateTime).getTime();
      return Number.isFinite(recordMs) ? Math.max(max, recordMs) : max;
    }, -Infinity);

    if (!Number.isFinite(newestMs)) {
      throw new Error(
        `Cannot page: no usable dateTime in a full page of StatusData for ${describeSeries(task)}`,
      );
    }

    const nextFromDate = new Date(newestMs + 1);
    if (nextFromDate.getTime() <= pageFromDate.getTime()) {
      throw new Error(
        `Cannot page: no progress after a full page of StatusData for ${describeSeries(task)} (newest record ${new Date(newestMs).toISOString()} is not after the requested fromDate ${pageFromDate.toISOString()})`,
      );
    }
    // `toDate` is inclusive, so a cursor that lands exactly on it still has a page to fetch.
    if (nextFromDate.getTime() > toDate.getTime()) {
      return;
    }
    pageFromDate = nextFromDate;
  }
}

/**
 * A series across many vehicles (a group or the whole fleet), paged the way Geotab documents:
 * sorted by date, each page continuing after the last record's `dateTime` and `id`.
 *
 * Moving `fromDate` forward, as a single vehicle's series does, assumes the page is ordered by
 * date across vehicles, which Geotab does not promise without a sort, and it loses records that
 * share a millisecond with the page's last one — across a fleet that is no longer rare. `lastId`
 * is required here because the date is not unique.
 */
async function fetchSortedSeries(
  geotabClient: Geotab,
  task: StatusDataFetchTask,
  fromDate: Date,
  toDate: Date,
  resultsLimit: number,
  onPage: FetchStatusDataCompleteOptions["onPage"],
  signal: AbortSignal,
): Promise<void> {
  const search = {
    ...deviceSearchOf(task),
    ...(task.diagnosticId ? { diagnosticSearch: { id: task.diagnosticId } } : {}),
    fromDate,
    toDate,
  };
  let cursor: { offset: Date | string; lastId: string } | undefined;

  while (true) {
    signal.throwIfAborted();

    const response = await geotabClient.call<StatusData[]>(
      "Get",
      {
        typeName: "StatusData",
        search,
        resultsLimit,
        sort: { sortBy: "date", sortDirection: "asc", ...cursor },
      },
      signal,
    );
    const page = checkedPage(response, task, signal);
    onPage(task.key, page);

    if (page.length < resultsLimit) {
      return;
    }

    // Interpolated records carry no id and cannot continue a page, so the cursor is the last
    // stored one.
    const last = [...page].reverse().find(record => !!record.id && !!record.dateTime);
    if (!last) {
      throw new Error(
        `Cannot page: no record with an id and dateTime in a full page of StatusData for ${describeSeries(task)}`,
      );
    }
    if (cursor && last.id === cursor.lastId) {
      throw new Error(
        `Cannot page: no progress after a full page of StatusData for ${describeSeries(task)}`,
      );
    }
    cursor = { offset: last.dateTime, lastId: last.id };
  }
}

function checkedPage(
  response: unknown,
  task: StatusDataFetchTask,
  signal: AbortSignal,
): StatusData[] {
  if (!Array.isArray(response)) {
    throw new Error(
      `Unexpected StatusData response for ${describeSeries(task)}: expected an array, got ${String(response)}`,
    );
  }
  // A Get that was already in flight when the scan was aborted can still resolve. Its page
  // belongs to a scan nobody is waiting for any more and must not reach the caller.
  signal.throwIfAborted();
  return response as StatusData[];
}

function describeSeries(task: StatusDataFetchTask): string {
  const device = task.deviceId
    ? `device ${task.deviceId}`
    : task.groupIds?.length
      ? `groups ${task.groupIds.join(", ")}`
      : "all devices";
  return task.diagnosticId ? `${device} / diagnostic ${task.diagnosticId}` : device;
}
